import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle, XCircle, Loader2, ShieldCheck, User } from "lucide-react";
import api from "../services/api"; // Will fail if api expects token, but we assume it handles public routes ok or we can use fetch

export default function VerifyStudent() {
  const { studentId } = useParams();
  const [loading, setLoading] = useState(true);
  const [student, setStudent] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // We can use native fetch or our api interceptor. Since it's public, fetch is safer to avoid token 401s if misconfigured.
    const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
    fetch(`${baseUrl}/users/public/verify/${studentId}`)
      .then(res => {
        if (!res.ok) throw new Error("Verification failed or student not found.");
        return res.json();
      })
      .then(data => setStudent(data))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [studentId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center font-['Inter',sans-serif]">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin mb-4" />
        <h2 className="text-xl font-bold text-slate-700">Verifying Identity...</h2>
      </div>
    );
  }

  if (error || !student) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center font-['Inter',sans-serif] p-4 text-center">
        <div className="w-24 h-24 bg-red-100 text-red-600 rounded-full flex items-center justify-center mb-6 shadow-lg shadow-red-100">
           <XCircle className="w-12 h-12" />
        </div>
        <h1 className="text-3xl font-black text-slate-900 tracking-tight mb-2">Verification Failed</h1>
        <p className="text-slate-500 font-medium mb-8 max-w-md">{error}</p>
        <Link to="/" className="px-6 py-3 bg-slate-900 text-white font-bold rounded-xl hover:bg-slate-800 transition-colors">
          Return to Home
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4 font-['Inter',sans-serif] flex items-center justify-center">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl border border-slate-100 overflow-hidden relative">
        
        {/* Top Header */}
        <div className={`p-8 text-center ${student.is_active ? 'bg-emerald-600' : 'bg-amber-500'}`}>
           <div className="mx-auto w-16 h-16 bg-white rounded-full flex items-center justify-center mb-4 shadow-lg text-emerald-600">
              <ShieldCheck className="w-8 h-8" />
           </div>
           <h1 className="text-3xl font-black text-white tracking-tight">
             {student.is_active ? "Verified Student" : "Account Inactive"}
           </h1>
           <p className="text-emerald-100 font-medium mt-1">Official Institution Record</p>
        </div>

        {/* Profile Card */}
        <div className="p-8 relative">
           {/* Floating Avatar */}
           <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-24 h-24 bg-white rounded-full p-1 shadow-xl">
              {student.profile_picture ? (
                 <img src={student.profile_picture} alt={student.full_name} className="w-full h-full rounded-full object-cover" />
              ) : (
                 <div className="w-full h-full rounded-full bg-slate-100 text-slate-400 flex items-center justify-center">
                    <User className="w-10 h-10" />
                 </div>
              )}
           </div>

           <div className="text-center mt-12 mb-8">
              <h2 className="text-2xl font-black text-slate-900 uppercase">{student.full_name}</h2>
              <p className="text-sm font-bold text-slate-500 uppercase tracking-widest">{student.institute_name}</p>
           </div>

           <div className="space-y-4">
              <div className="flex justify-between items-center py-3 border-b border-slate-100">
                 <span className="text-sm font-bold text-slate-400 uppercase tracking-widest">System ID</span>
                 <span className="font-mono font-bold text-slate-900">#{student.id}</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-slate-100">
                 <span className="text-sm font-bold text-slate-400 uppercase tracking-widest">Roll Number</span>
                 <span className="font-bold text-slate-900">{student.roll_number || "N/A"}</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-slate-100">
                 <span className="text-sm font-bold text-slate-400 uppercase tracking-widest">Enrolled Course</span>
                 <span className="font-bold text-slate-900 text-right">{student.course_name || "General"}</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-slate-100">
                 <span className="text-sm font-bold text-slate-400 uppercase tracking-widest">Batch</span>
                 <span className="font-bold text-slate-900">{student.batch || "N/A"}</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-slate-100">
                 <span className="text-sm font-bold text-slate-400 uppercase tracking-widest">Status</span>
                 {student.is_active ? (
                    <span className="px-3 py-1 bg-emerald-100 text-emerald-700 text-xs font-black rounded-full uppercase tracking-wider flex items-center gap-1">
                      <CheckCircle size={12}/> Active
                    </span>
                 ) : (
                    <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-black rounded-full uppercase tracking-wider flex items-center gap-1">
                      Inactive
                    </span>
                 )}
              </div>
           </div>

           <div className="mt-8 text-center">
             <Link to="/" className="text-sm font-bold text-blue-600 hover:text-blue-800 underline">
                Go to Portal
             </Link>
           </div>
        </div>
      </div>
    </div>
  );
}
